import { supabase } from './supabase';

export interface Profile {
  id: string;
  role: string;
  otp_validated: boolean;
  credits: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  professional_card: string;
  company_name: string;
  company_email: string;
  company_phone: string;
  logo_image: string | null;
  signature_image: string | null;
}

// Variable global para almacenar el perfil actual
let currentProfile: Profile | null = null;
let loadingPromise: Promise<Profile | null> | null = null;

// Función para obtener el perfil del usuario
export const fetchUserProfile = async (userId: string): Promise<Profile | null> => {
  console.log('🔄 fetchUserProfile llamado con userId:', userId, 'Stack:', new Error().stack);
  try {
    // Si ya tenemos el perfil cargado y es del mismo usuario, lo retornamos
    if (currentProfile && currentProfile.id === userId) {
      console.log('✅ Usando perfil en caché para:', userId);
      return currentProfile;
    }

    // Si hay una promesa de carga en curso, la reutilizamos
    if (loadingPromise) {
      console.log('⏳ Reutilizando promesa de carga existente');
      return loadingPromise;
    }

    console.log('📡 Obteniendo perfil del usuario desde la base de datos:', userId);
    loadingPromise = (async () => {
      try {
        const { data: profile, error } = await supabase()
          .from('profiles')
          .select('id, role, otp_validated, credits, email, first_name, last_name, full_name, phone, professional_card, company_name, company_email, company_phone, logo_image, signature_image')
          .eq('id', userId)
          .single();

        if (error) {
          console.error('❌ Error al obtener perfil:', error);
          return null;
        }

        if (!profile) {
          console.log('⚠️ No se encontró el perfil del usuario');
          return null;
        }

        console.log('✅ Perfil obtenido de la base de datos:', profile);
        console.log('Tipo de role:', typeof profile.role, 'Valor:', profile.role);

        const formattedProfile: Profile = {
          id: String(profile.id),
          role: String(profile.role),
          otp_validated: Boolean(profile.otp_validated),
          credits: Number(profile.credits) || 0,
          email: String(profile.email),
          first_name: String(profile.first_name),
          last_name: String(profile.last_name),
          full_name: String(profile.full_name),
          phone: String(profile.phone),
          professional_card: String(profile.professional_card || ''),
          company_name: String(profile.company_name || ''),
          company_email: String(profile.company_email || ''),
          company_phone: String(profile.company_phone || ''),
          logo_image: profile.logo_image ? String(profile.logo_image) : null,
          signature_image: profile.signature_image ? String(profile.signature_image) : null,
        };

        console.log('✅ Perfil formateado y almacenado en caché:', formattedProfile);
        console.log('Verificación final de super_admin:', {
          originalRole: profile.role,
          formattedRole: formattedProfile.role,
          isSuperAdmin: formattedProfile.role === 'super_admin'
        });

        // Almacenar el perfil formateado en la caché
        currentProfile = formattedProfile;
        return formattedProfile;
      } finally {
        // Limpiar la promesa de carga después de completar
        loadingPromise = null;
      }
    })();

    return loadingPromise;
  } catch (error) {
    console.error('❌ Error en fetchUserProfile:', error);
    loadingPromise = null;
    return null;
  }
};

// Función para obtener solo los créditos del usuario
export const getUserCredits = async (userId: string): Promise<number> => {
  const profile = await fetchUserProfile(userId);
  return profile?.credits || 0;
};

// Función para limpiar el perfil en caché
export const clearProfileCache = () => {
  currentProfile = null;
  loadingPromise = null;
}; 